import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import ExportButton from "../../components/ExportButton";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Bell } from "lucide-react";

export default function Reports() {
  const { user } = useAuth();
  const isPartner = user?.role === "partner";
  const canRemind = ["partner", "admin"].includes(user?.role);
  const canCost = ["partner", "admin"].includes(user?.role);
  const [data, setData] = useState(null);
  const [timesheets, setTimesheets] = useState([]);
  const [reminding, setReminding] = useState(false);
  const [costMonth, setCostMonth] = useState(new Date().toISOString().slice(0,7));
  const [cost, setCost] = useState(null);
  const [clientCostRange, setClientCostRange] = useState({
    start: new Date(new Date().setDate(1)).toISOString().slice(0,10),
    end: new Date().toISOString().slice(0,10),
  });
  const [clientCost, setClientCost] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data));
    api.get("/timesheets?scope=all").then((r) => setTimesheets(r.data));
  }, []);

  useEffect(() => {
    if (canCost) api.get(`/reports/monthly-cost?month=${costMonth}`).then((r) => setCost(r.data)).catch(() => setCost(null));
  }, [canCost, costMonth]);

  useEffect(() => {
    if (canCost) api.get(`/reports/client-cost?start_date=${clientCostRange.start}&end_date=${clientCostRange.end}`).then((r) => setClientCost(r.data)).catch(() => setClientCost(null));
  }, [canCost, clientCostRange]);

  const sendReminders = async () => {
    setReminding(true);
    try {
      const { data } = await api.post("/timesheets/remind");
      toast.success(`Reminder sent to ${data.sent} of ${data.defaulters} defaulter(s)`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setReminding(false); }
  };

  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;

  const byProject = {};
  timesheets.forEach((t) => {
    const k = `${t.client_name || "—"} · ${t.project_name || "—"}`;
    if (!byProject[k]) byProject[k] = { billable: 0, non_billable: 0, total: 0 };
    byProject[k].total += t.hours;
    if (t.billable) byProject[k].billable += t.hours; else byProject[k].non_billable += t.hours;
  });

  return (
    <div className="space-y-6 fade-in" data-testid="reports-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Reports</div>
          <h1 className="font-sans text-4xl mt-2">Analytics & insights</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {canRemind && <Button onClick={sendReminders} disabled={reminding} variant="outline" className="rounded-sm border-slate-300" data-testid="send-reminders"><Bell className="h-4 w-4 mr-2"/>{reminding ? "Sending..." : "Send Weekly Reminders"}</Button>}
          <ExportButton path="/exports/timesheets" filename="timesheets_all.csv" label="Export Timesheets" testId="reports-ts-export"/>
          <ExportButton path="/exports/reimbursements" filename="reimbursements_all.csv" label="Export Claims" testId="reports-rmb-export"/>
        </div>
      </div>

      {data.firm && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 stagger">
          <div className="bg-white border border-slate-200 rounded-sm p-6"><div className="eyebrow">Total Billable · MTD</div><div className="serif-num text-5xl mt-3">{data.firm.billable_hours_month}</div></div>
          <div className="bg-white border border-slate-200 rounded-sm p-6"><div className="eyebrow">Non-Billable · MTD</div><div className="serif-num text-5xl mt-3">{data.firm.non_billable_hours_month}</div></div>
          {isPartner && <div className="bg-white border border-slate-200 rounded-sm p-6"><div className="eyebrow">Utilization</div><div className="serif-num text-5xl mt-3">{data.firm.utilization_pct}%</div></div>}
          <div className="bg-white border border-slate-200 rounded-sm p-6"><div className="eyebrow">Active Projects</div><div className="serif-num text-5xl mt-3">{data.firm.active_projects}</div></div>
        </div>
      )}

      {canCost && (
        <div className="bg-white border border-slate-200 rounded-sm" data-testid="monthly-cost-section">
          <div className="p-6 border-b border-slate-200 flex items-end justify-between gap-4 flex-wrap">
            <div>
              <div className="eyebrow">Monthly Cost</div>
              <h2 className="font-sans text-2xl mt-1">Salary & reimbursement summary</h2>
            </div>
            <Input type="month" value={costMonth} onChange={(e)=>setCostMonth(e.target.value)} className="rounded-sm w-40" data-testid="cost-month"/>
          </div>
          {cost && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 border-b border-slate-200">
                <div className="bg-white p-6"><div className="eyebrow">Net Salary</div><div className="serif-num text-3xl mt-2">₹{cost.salary_total.toLocaleString("en-IN")}</div></div>
                <div className="bg-white p-6"><div className="eyebrow">Gross Salary</div><div className="serif-num text-3xl mt-2">₹{cost.salary_gross.toLocaleString("en-IN")}</div></div>
                <div className="bg-white p-6"><div className="eyebrow">Reimbursements</div><div className="serif-num text-3xl mt-2">₹{cost.reimbursement_total.toLocaleString("en-IN")}</div><div className="text-xs text-slate-500 mt-1">Paid: ₹{cost.reimbursement_paid.toLocaleString("en-IN")}</div></div>
                <div className="bg-white p-6"><div className="eyebrow">Grand Total</div><div className="serif-num text-3xl mt-2 text-[#946f30]">₹{cost.grand_total.toLocaleString("en-IN")}</div></div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
                  <tr><th className="text-left p-4">Code</th><th className="text-left p-4">Employee</th><th className="text-right p-4">Salary (Net)</th><th className="text-right p-4">Reimbursement</th><th className="text-right p-4 font-semibold">Total Cost</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-testid="monthly-cost-table">
                  {cost.rows.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-500">No data for {costMonth}. Generate payroll first.</td></tr>}
                  {cost.rows.map((r) => (
                    <tr key={r.user_id} className="hover:bg-slate-50">
                      <td className="p-4 font-mono text-xs">{r.employee_code || "—"}</td>
                      <td className="p-4 font-medium">{r.user_name || "—"}</td>
                      <td className="p-4 text-right serif-num">₹{(r.salary_net||0).toLocaleString("en-IN")}</td>
                      <td className="p-4 text-right serif-num">₹{(r.reimbursement||0).toLocaleString("en-IN")}</td>
                      <td className="p-4 text-right serif-num font-semibold">₹{(r.total_cost||0).toLocaleString("en-IN")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="p-6 border-b border-slate-200 eyebrow">Client · Project hours summary</div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
            <tr><th className="text-left p-4">Client / Project</th><th className="text-right p-4">Billable</th><th className="text-right p-4">Non-Billable</th><th className="text-right p-4">Total</th><th className="text-right p-4">Bill %</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-testid="reports-table">
            {Object.keys(byProject).length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-500">No data yet.</td></tr>}
            {Object.entries(byProject).map(([name, v]) => (
              <tr key={name} className="hover:bg-slate-50">
                <td className="p-4 font-medium">{name}</td>
                <td className="p-4 text-right serif-num">{v.billable.toFixed(1)}</td>
                <td className="p-4 text-right serif-num">{v.non_billable.toFixed(1)}</td>
                <td className="p-4 text-right serif-num">{v.total.toFixed(1)}</td>
                <td className="p-4 text-right text-slate-600">{v.total > 0 ? ((v.billable / v.total) * 100).toFixed(0) : 0}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {canCost && (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="client-cost-report">
          <div className="p-6 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
            <div className="eyebrow">Client-wise cost incurred</div>
            <div className="flex items-center gap-3 flex-wrap">
              <Label className="text-xs text-slate-500">From</Label>
              <Input type="date" value={clientCostRange.start} onChange={(e)=>setClientCostRange({...clientCostRange, start: e.target.value})} className="w-40 rounded-sm" data-testid="client-cost-start"/>
              <Label className="text-xs text-slate-500">To</Label>
              <Input type="date" value={clientCostRange.end} onChange={(e)=>setClientCostRange({...clientCostRange, end: e.target.value})} className="w-40 rounded-sm" data-testid="client-cost-end"/>
            </div>
          </div>
          {clientCost && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-slate-200">
                <div className="bg-white p-5"><div className="eyebrow">Manpower Cost</div><div className="serif-num text-3xl mt-2">₹{(clientCost.total_manpower_cost || 0).toLocaleString("en-IN")}</div></div>
                <div className="bg-white p-5"><div className="eyebrow">Reimbursements</div><div className="serif-num text-3xl mt-2">₹{(clientCost.total_reimbursement_cost || 0).toLocaleString("en-IN")}</div></div>
                <div className="bg-white p-5"><div className="eyebrow">Grand Total</div><div className="serif-num text-3xl mt-2 text-emerald-700">₹{(clientCost.grand_total || 0).toLocaleString("en-IN")}</div></div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
                  <tr><th className="text-left p-4">Code</th><th className="text-left p-4">Client</th><th className="text-right p-4">Billable Hrs</th><th className="text-right p-4">Internal Hrs</th><th className="text-right p-4">Manpower Cost</th><th className="text-right p-4">Reimb.</th><th className="text-right p-4 font-semibold">Total Cost</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-testid="client-cost-table">
                  {(clientCost.rows || []).length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-500">No data for selected range.</td></tr>}
                  {(clientCost.rows || []).map((r) => (
                    <tr key={r.client_id} className="hover:bg-slate-50">
                      <td className="p-4 font-mono text-xs">{r.client_code}</td>
                      <td className="p-4 font-medium">{r.client_name}</td>
                      <td className="p-4 text-right serif-num">{r.billable_hours.toFixed(1)}</td>
                      <td className="p-4 text-right serif-num text-slate-500">{r.internal_hours.toFixed(1)}</td>
                      <td className="p-4 text-right serif-num">₹{r.manpower_cost.toLocaleString("en-IN")}</td>
                      <td className="p-4 text-right serif-num">₹{r.reimbursement_cost.toLocaleString("en-IN")}</td>
                      <td className="p-4 text-right serif-num font-semibold">₹{r.total_cost.toLocaleString("en-IN")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </div>
  );
}
